import { ApiError } from "../api/http_client.js";
import { AuthSessionStore } from "../api/auth_session.js";
import {
  completeLevel,
  doubleLevelReward,
  purchaseItem,
  reportLevelFailed,
  reportLevelStart,
  rewardBooster,
  rewardHeart,
} from "../api/requests/economy.requests.js";
import { consumeBooster, purchaseBooster } from "../api/requests/cumulative_boosters.requests.js";
import { claimDailyTask, claimStarChest } from "../api/requests/daily_tasks.requests.js";
import { completeDailyChallengeStage, getDailyChallenge } from "../api/requests/daily_challenge.requests.js";
import { claimWinStreakTowerFloor, getWinStreakTower } from "../api/requests/win_streak_tower.requests.js";
import { chooseKeyHuntPrize, claimKeyHuntPrize, claimKeyHuntTask, getKeyHunt } from "../api/requests/key_hunt.requests.js";
import { KeyHuntService } from "../key_hunt/key_hunt_service.js";
import { getPlayer } from "../api/requests/player.requests.js";
import type { PurchaseItem, RewardBoosterType } from "../api/dto/player.dto.js";
import type { CumulativeBoosterID } from "../cumulative_boosters/cumulative_booster_id.js";
import { adoptEconomyFromPlayer, syncProfileFromServer } from "../api/player_sync.js";
import { NetworkManager } from "../network_manager.js";
import { StatisticsStorage } from "../storage/statistics_storage.js";
import { LevelPackService } from "../level_pack/level_pack_service.js";
import { DailyTasksService } from "../daily_tasks/daily_tasks_service.js";
import { DailyChallengeService } from "../daily_challenge/daily_challenge_service.js";
import { WinStreakTowerService } from "../win_streak_tower/win_streak_tower_service.js";
import { ConfigSyncService } from "../config_sync/config_sync_service.js";
import { isNetworkishError } from "../utils/network_errors.js";
import { OfflineJournal, isAtMostOnceAction, type OfflineAction } from "./offline_journal.js";
import { ServerClock } from "./server_clock.js";
import { crashCapture } from "../crash/crash_reporter.js";
import { TournamentService } from "../tournament/tournament_service.js";
import { claimPrizeShot, continuePrizeShot } from "../api/requests/prize_shot.requests.js";
import type { PrizeShotReward } from "../prize_shot/prize_shot_rules.js";

export interface ReplayStats
{
  replayed: number;
  dropped: number;
  stopped: boolean;
}

type SyncListener = ( phase: "start" | "done", stats: ReplayStats | null ) => void;

class ReplayContext
{
  private challenge_date: Promise<string | null> | null = null;
  private tower_period: Promise<string | null> | null = null;
  private key_hunt_period: Promise<string | null> | null = null;

  public currentChallengeDate(): Promise<string | null>
  {
    if ( !this.challenge_date )
      this.challenge_date = getDailyChallenge().then( p => p.date ?? null ).catch( e => { throw e; } );
    return this.challenge_date;
  }

  public currentTowerPeriod(): Promise<string | null>
  {
    if ( !this.tower_period )
      this.tower_period = getWinStreakTower().then( p => p.tower?.period_start ?? null );
    return this.tower_period;
  }

  public async assertKeyHuntPeriod( period_start: unknown, what: string ): Promise<void>
  {
    if ( !this.key_hunt_period )
      this.key_hunt_period = getKeyHunt().then( p => p.key_hunt?.period_start ?? null );
    const current = await this.key_hunt_period;
    if ( typeof period_start === "string" && current !== null && current !== period_start )
      throw new StaleActionError( `${what} belongs to key hunt period ${period_start}` );
  }
}

class StaleActionError extends Error
{
  constructor( message: string )
  {
    super( message );
    this.name = "StaleActionError";
  }
}

function occurredAt( action: OfflineAction ): string | undefined
{
  const ms = Number( action.created_at );
  return Number.isFinite( ms ) && ms > 0 ? new Date( ms + ServerClock.getOffsetMs() ).toISOString() : undefined;
}

async function dispatch( action: OfflineAction, ctx: ReplayContext ): Promise<void>
{
  const p = action.payload;
  switch ( action.type )
  {
    case "level_start":
      await reportLevelStart( Number( p.level ), typeof p.app_run_id === "string" ? p.app_run_id : undefined, occurredAt( action ) );
      return;
    case "level_complete":
      await completeLevel( Number( p.level ), Number( p.stars ) || 3 );
      return;
    case "level_failed":
      await reportLevelFailed( occurredAt( action ) );
      return;
    case "level_double_reward":
      await doubleLevelReward( Number( p.level ), Number( p.stars ) || 3 );
      return;
    case "purchase":
      await purchaseItem( p.item as PurchaseItem, {
        ...( typeof p.purchase_index === "number" ? { purchase_index: p.purchase_index } : {} ),
        price: Number( p.price ),
        qty: Number( p.qty ),
      } );
      return;
    case "booster_consume":
      await consumeBooster( p.booster_id as CumulativeBoosterID );
      return;
    case "booster_purchase":
      await purchaseBooster( p.booster_id as CumulativeBoosterID, Number( p.offer_id ) );
      return;
    case "reward_heart":
      await rewardHeart();
      return;
    case "reward_booster":
      await rewardBooster( p.booster_type as RewardBoosterType, String( p.placement ), String( p.ad_id ) );
      return;
    case "daily_task_claim":
      await claimDailyTask( String( p.task_id ), typeof p.day === "number" ? p.day : undefined );
      return;
    case "star_chest_claim":
      await claimStarChest( Number( p.index ) );
      return;
    case "daily_challenge_complete":
    {
      const current = await ctx.currentChallengeDate();
      if ( typeof p.date === "string" && current !== null && current !== p.date )
        throw new StaleActionError( `challenge stage ${p.stage} belongs to ${p.date}, today is ${current}` );
      await completeDailyChallengeStage( Number( p.stage ) );
      return;
    }
    case "tower_floor_claim":
    {
      const current = await ctx.currentTowerPeriod();
      if ( typeof p.period_start === "string" && current !== null && current !== p.period_start )
        throw new StaleActionError( `tower floor ${p.floor_id} belongs to period ${p.period_start}` );
      await claimWinStreakTowerFloor( Number( p.floor_id ) );
      return;
    }
    case "key_hunt_task_claim":
      await ctx.assertKeyHuntPeriod( p.period_start, `key hunt task ${p.task_id}` );
      await claimKeyHuntTask( String( p.task_id ) );
      return;
    case "key_hunt_prize_choose":
      await ctx.assertKeyHuntPeriod( p.period_start, "key hunt prize choice" );
      await chooseKeyHuntPrize( Number( p.index ) );
      return;
    case "key_hunt_prize_claim":
      await ctx.assertKeyHuntPeriod( p.period_start, "key hunt prize" );
      await claimKeyHuntPrize();
      return;
    // The server checks the round (period) itself: a stale one is a 4xx and the step is dropped.
    case "prize_shot_continue":
      await continuePrizeShot( String( p.round_id ), Number( p.continue_index ), Number( p.price ), Number( p.shots ) );
      return;
    case "prize_shot_claim":
      await claimPrizeShot( String( p.round_id ), Array.isArray( p.rewards ) ? p.rewards as PrizeShotReward[] : [] );
      return;
  }
}

const MAX_REPLAY_PASSES = 5;

export class OfflineSyncService
{
  private static syncing: Promise<void> | null = null;
  private static replaying: Promise<ReplayStats> | null = null;
  private static rerun_requested = false;
  private static listeners = new Set<SyncListener>();
  private static last_stats: ReplayStats | null = null;

  public static subscribe( listener: SyncListener ): () => void
  {
    this.listeners.add( listener );
    return () => this.listeners.delete( listener );
  }

  public static isSyncing(): boolean
  {
    return this.syncing !== null;
  }

  public static isReplaying(): boolean
  {
    return this.replaying !== null;
  }

  public static hasPending(): boolean
  {
    return OfflineJournal.hasPending();
  }

  public static getLastStats(): ReplayStats | null
  {
    return this.last_stats;
  }

  public static sync( reason: string ): Promise<void>
  {
    if ( this.syncing )
    {
      this.rerun_requested = true;
      return this.syncing;
    }

    this.syncing = ( async () =>
    {
      try
      {
        do
        {
          this.rerun_requested = false;
          await this.syncOnce( reason );
        }
        while ( this.rerun_requested && NetworkManager.isOnline() );
      }
      finally
      {
        this.syncing = null;
      }
    } )();

    return this.syncing;
  }

  private static async syncOnce( reason: string ): Promise<void>
  {
    if ( !NetworkManager.isOnline() || !AuthSessionStore.isLoggedIn() )
      return;

    const pending_before = OfflineJournal.count();
    if ( pending_before === 0 )
      return;

    console.info( `[OfflineSync] ${reason}: replaying ${pending_before} action(s)` );
    this.emit( "start", null );

    let stats = await this.replayPending();
    for ( let pass = 1; !stats.stopped && OfflineJournal.hasPending() && pass < MAX_REPLAY_PASSES; pass++ )
    {
      const more = await this.replayPending();
      stats = { replayed: stats.replayed + more.replayed, dropped: stats.dropped + more.dropped, stopped: more.stopped };
    }
    this.last_stats = stats;
    console.info( `[OfflineSync] replayed ${stats.replayed}, dropped ${stats.dropped}${stats.stopped ? ", stopped on network error" : ""}` );

    if ( !stats.stopped && OfflineJournal.hasPending() )
    {
      this.rerun_requested = true;
      this.emit( "done", stats );
      return;
    }

    if ( !stats.stopped )
      await this.refreshAfterSync();

    this.emit( "done", stats );
  }

  public static replayPending(): Promise<ReplayStats>
  {
    if ( this.replaying ) return this.replaying;
    this.replaying = this.replayPass().finally( () => { this.replaying = null; } );
    return this.replaying;
  }

  private static async replayPass(): Promise<ReplayStats>
  {
    const stats: ReplayStats = { replayed: 0, dropped: 0, stopped: false };
    const ctx = new ReplayContext();

    const current_player = AuthSessionStore.getPlayerId();
    for ( const action of [ ...OfflineJournal.list() ] )
    {
      if ( action.player_id != null && current_player != null && action.player_id !== current_player )
      {
        console.warn( `[OfflineSync] dropping ${action.type} journaled by player ${action.player_id}, now logged in as ${current_player}` );
        OfflineJournal.remove( action.id );
        stats.dropped++;
        continue;
      }

      if ( action.state === "inflight" && isAtMostOnceAction( action.type ) )
      {
        console.warn( `[OfflineSync] ${action.type} was in flight when the app died — assuming applied` );
        OfflineJournal.remove( action.id );
        stats.dropped++;
        continue;
      }

      OfflineJournal.markInflight( action.id );
      try
      {
        await dispatch( action, ctx );
        OfflineJournal.remove( action.id );
        stats.replayed++;
      }
      catch ( e )
      {
        if ( e instanceof StaleActionError || e instanceof ApiError )
        {
          console.warn( `[OfflineSync] dropped ${action.type}:`, e instanceof ApiError ? `${e.status} ${e.message}` : e.message, action.payload );
          OfflineJournal.remove( action.id );
          stats.dropped++;
          continue;
        }

        if ( isNetworkishError( e ) )
        {
          OfflineJournal.markPending( action.id );
          NetworkManager.handleNetworkError( e );
          stats.stopped = true;
          break;
        }

        console.error( `[OfflineSync] unexpected failure on ${action.type} — dropping it`, e, action.payload );
        crashCapture( e, { phase: "offline_replay", action_type: action.type } );
        OfflineJournal.remove( action.id );
        stats.dropped++;
      }
    }

    return stats;
  }

  public static async refreshAfterSync(): Promise<void>
  {
    try
    {
      const player = await getPlayer();
      syncProfileFromServer( player );
      adoptEconomyFromPlayer( player );
    }
    catch ( e )
    {
      console.warn( "[OfflineSync] player refresh failed", e );
    }

    await Promise.all( [
      DailyTasksService.fetch( true ),
      DailyChallengeService.fetch( true ),
      WinStreakTowerService.fetch( true ),
      KeyHuntService.fetch( true ),
      TournamentService.fetch( true ),
      ConfigSyncService.refreshStale( "offline_sync" ),
    ] ).catch( e => console.warn( "[OfflineSync] event refresh failed", e ) );

    await OfflineSyncService.warmOfflineContent();
  }

  public static async warmOfflineContent(): Promise<void>
  {
    try
    {
      await LevelPackService.refresh();
      await LevelPackService.prefetchOfflineWindow( StatisticsStorage.load().level );
    }
    catch ( e )
    {
      console.warn( "[OfflineSync] level prefetch failed", e );
    }
    void DailyChallengeService.prefetchStageLevels();
  }

  private static emit( phase: "start" | "done", stats: ReplayStats | null ): void
  {
    for ( const listener of this.listeners )
    {
      try { listener( phase, stats ); } catch ( e ) { console.warn( "[OfflineSync] listener failed", e ); }
    }
  }
}
